import { trackError } from './analytics';

/**
 * Error reporting for a local-first app.
 *
 * The constraint that shapes this: the app's promise is that your content
 * never reaches our servers. An error reporter that ships stack traces
 * containing task titles, note text or file paths would quietly break that
 * promise — and the privacy policy that now describes it.
 *
 * So reports carry the *shape* of a failure and nothing else: error name,
 * a scrubbed message, the function names from the stack, which view it
 * happened in, and the app version. No user content, ever.
 *
 * Transport is pluggable. Today it goes to GA4 as an `exception` event, which
 * is already disclosed in the privacy policy and costs nothing. Swapping in a
 * real error service later means calling setErrorTransport() — and adding a
 * line to the privacy policy, because that would be a new data processor.
 */

export interface ErrorReport {
  /** Error constructor name, e.g. "TypeError". */
  name: string;
  /** Scrubbed message — see scrubMessage. */
  message: string;
  /** Function names from the stack, without file paths or arguments. */
  frames: string[];
  /** Where in the app it happened (view id, or a component name). */
  context: string;
  /** How it reached us: a render crash, a stray throw, a dropped promise. */
  source: 'boundary' | 'window' | 'promise' | 'manual';
  appVersion: string;
  /** Time on the user's clock; useful when they paste a report into a bug. */
  at: string;
}

type Transport = (report: ErrorReport) => void;

let currentContext = 'app';
/** Reports already sent this session, so one loop can't flood anything. */
const seen = new Set<string>();
const MAX_REPORTS_PER_SESSION = 25;
let sent = 0;

/**
 * Strip anything that could be user content from a message.
 *
 * Messages routinely interpolate values — "Cannot read properties of
 * undefined (reading 'x')" is fine, but "List 'Christmas presents for Anna'
 * not found" is not. Quoted runs, URLs, emails and long tokens are replaced;
 * what's left is the structural part that actually helps diagnose.
 */
export function scrubMessage(raw: string): string {
  return raw
    .replace(/https?:\/\/\S+/g, '<url>')
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '<email>')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '<id>')
    .replace(/'[^']{0,200}'/g, (m) => (m.length > 40 ? "'<value>'" : m))
    .replace(/"[^"]{0,200}"/g, (m) => (m.length > 40 ? '"<value>"' : m))
    .slice(0, 300);
}

/**
 * Reduce a stack to bare function names.
 *
 * A raw stack contains URLs with query strings and, in some browsers,
 * source-mapped paths. Only the function names are kept — enough to locate
 * the failure, nothing that could carry data.
 */
export function scrubStack(stack: string | undefined, limit = 8): string[] {
  if (!stack) return [];
  const frames: string[] = [];
  for (const line of stack.split('\n').slice(1)) {
    const match = line.match(/at\s+([\w$.<>]+)\s*\(/) ?? line.match(/^\s*([\w$.<>]+)@/);
    const name = match?.[1];
    if (name && name !== 'Object.<anonymous>') frames.push(name);
    if (frames.length >= limit) break;
  }
  return frames;
}

/** GA4 is already a disclosed processor, so it is the default sink. */
const gaTransport: Transport = (report) => {
  trackError(report.name, `${report.context}: ${report.message}`);
};

let transport: Transport = gaTransport;

/** Replace the sink (e.g. with a real error service). */
export function setErrorTransport(next: Transport): void {
  transport = next;
}

/** Tell the reporter which view the user is on, so reports say where. */
export function setErrorContext(context: string): void {
  currentContext = context;
}

export function reportError(
  error: unknown,
  options: { context?: string; source?: ErrorReport['source'] } = {},
): ErrorReport | null {
  const err = error instanceof Error ? error : new Error(String(error));
  const report: ErrorReport = {
    name: err.name || 'Error',
    message: scrubMessage(err.message || 'Unknown error'),
    frames: scrubStack(err.stack),
    context: options.context ?? currentContext,
    source: options.source ?? 'manual',
    appVersion: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown',
    at: new Date().toISOString(),
  };

  // A render loop can throw the same error hundreds of times a second.
  const key = `${report.name}|${report.message}|${report.frames[0] ?? ''}`;
  if (seen.has(key) || sent >= MAX_REPORTS_PER_SESSION) return report;
  seen.add(key);
  sent++;

  // The console keeps the FULL error for whoever is actually debugging on
  // this machine; only the scrubbed version is transmitted.
  console.error(`[${report.context}]`, err);

  try {
    transport(report);
  } catch {
    /* reporting must never be the thing that breaks the app */
  }
  return report;
}

/**
 * Catch what React's error boundaries can't: throws from event handlers,
 * async callbacks, and rejected promises nobody awaited.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    // Failed <img>/<script> loads also fire this, with no error object.
    if (!event.error) return;
    reportError(event.error, { source: 'window' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'promise' });
  });
}

/** Test seam: forget what has been reported so far. */
export function resetErrorReportingForTests(): void {
  seen.clear();
  sent = 0;
  currentContext = 'app';
  transport = gaTransport;
}
